import { useAtom } from "jotai";
import { useCallback } from "react";

import { useArtworkForm, usePlacementPosition } from "@design-lab/entities/artwork";
import { panFrontAtom, panBackAtom, zoomFrontAtom, zoomBackAtom } from "../model/state";

const MIN_ZOOM = 1;
const MAX_ZOOM = 10;

export function usePlacementZoomAndPan(minZoom = MIN_ZOOM, maxZoom = MAX_ZOOM) {
  const { lens } = useArtworkForm();
  const currentPlacementLens = lens.focus("currentPlacement");
  const currentPlacement = usePlacementPosition(currentPlacementLens);

  const isFrontPlacement =
    currentPlacement.placement === "front" || currentPlacement.placement === "frontLeftChest";

  const [zoomFront, setZoomFront] = useAtom(zoomFrontAtom);
  const [zoomBack, setZoomBack] = useAtom(zoomBackAtom);
  const zoom = isFrontPlacement ? zoomFront : zoomBack;
  const rawSetZoom = isFrontPlacement ? setZoomFront : setZoomBack;

  // Clamp before writing the atom so direct callers (gestures, buttons) cannot
  // push zoom outside the supported bounds — see MN-4132.
  const setZoom = useCallback(
    (value: number | ((prev: number) => number)) => {
      rawSetZoom(prev => {
        const next = typeof value === "function" ? value(prev) : value;
        // Ignore NaN/±Infinity so a degenerate gesture can't corrupt zoom.
        if (!Number.isFinite(next)) return prev;
        return Math.max(minZoom, Math.min(maxZoom, next));
      });
    },
    [rawSetZoom, minZoom, maxZoom]
  );

  const [frontPan, setFrontPan] = useAtom(panFrontAtom);
  const [backPan, setBackPan] = useAtom(panBackAtom);
  const pan = isFrontPlacement ? frontPan : backPan;
  const setPan = isFrontPlacement ? setFrontPan : setBackPan;

  return {
    zoom,
    pan,
    setZoom,
    setPan
  };
}
